import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export default function PaymentPortal({ amount: amountProp, readOnlyAmount = false, onSuccess }) {
  const { amount: amountParam } = useParams();
  // Prefer the passed-in prop; fall back to URL param if none provided
  const initialAmount = amountProp != null
    ? amountProp.toString()
    : (amountParam || '');
  const [amount, setAmount] = useState(initialAmount);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const [supportsPaymentRequest, setSupportsPaymentRequest] = useState(false);

  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'echeck'
  const [accountType, setAccountType] = useState('CHECKING'); // 'checking' or 'savings'
  const [holderType, setHolderType] = useState('personal'); // 'personal' or 'business'
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [eRouting, setERouting] = useState('');
  const [eAccount, setEAccount] = useState('');
  const [emailField, setEmailField] = useState('');
  const API_URL = process.env.REACT_APP_API_URL;
  useEffect(() => {
    if (window.PaymentRequest) {
      setSupportsPaymentRequest(true);
    }
  }, []);
    // Inside your component, alongside existing useState hooks:
    const [paymentsList, setPaymentsList] = useState([]);
    const [requiredFees, setRequiredFees] = useState(null);
    const [feesPaidPrior, setFeesPaidPrior] = useState(null);
    // Required retainer / down payment rules:
    // - if pre_filing_fee is $0 => $0
    // - if pre_filing_fee >= $1000 => $500
    // - otherwise => max($100, 1/2 pre_filing_fee), capped at pre_filing_fee
    const pre = Number(requiredFees || 0);
    let requiredDownPayment = 0;

    if (pre <= 0) {
      requiredDownPayment = 0;
    } else if (pre >= 1000) {
      requiredDownPayment = 500;
    } else {
      requiredDownPayment = Math.max(100, pre / 2);
      requiredDownPayment = Math.min(requiredDownPayment, pre);
    }
    const caseId = localStorage.getItem("clientCaseId");
    // In your existing useEffect (after fetchClientInfo):
    useEffect(() => {
    if (!caseId) return;
    const fetchPayments = async () => {
      try {
        const resp = await fetch(`${API_URL}/case/${caseId}/sofa-payments/`);
        if (!resp.ok) return;
        const json = await resp.json();
        // API now returns { payments, pre_filing_fees, fees_paid_prior }
        setPaymentsList(json.payments || []);
        // Safely parse and default to 0 if invalid
        const reqFees = parseFloat(json.pre_filing_fees);
        const paidPrior = parseFloat(json.fees_paid_prior);
        setRequiredFees(isNaN(reqFees) ? 0 : reqFees);
        setFeesPaidPrior(isNaN(paidPrior) ? 0 : paidPrior);
      } catch (err) {
        console.error(err);
      }
    };
    fetchPayments();
    }, [caseId]);
  // Fetch client info and populate fields
  const handleDeletePayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      const resp = await fetch(`${API_URL}/case/${caseId}/sofa-payments/${id}/`, {
        method: 'DELETE'
      });
      if (resp.ok) {
        setPaymentsList(paymentsList.filter(p => p.id !== id));
      } else {
        throw new Error('Delete failed');
      }
    } catch (err) {
      setError(err.message);
    }
  };
  const handleCashPayment = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        reference: caseId,
        amount,
        type: 'cash',
        email: emailField
      };
      const resp = await fetch(`${API_URL}/case/${caseId}/sofa-payments/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error('Failed to record cash payment');
      const newPayment = await resp.json();
      setPaymentsList([newPayment, ...paymentsList]);
      setSuccess(`Cash payment of $${amount} recorded.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!caseId) return;
    const fetchClientInfo = async () => {
      try {
        const resp = await fetch(`${API_URL}/case/${caseId}/client-info/`);
        if (!resp.ok) return;
        const json = await resp.json();
        if (json.first_name) setFirstName(json.first_name);
        if (json.last_name) setLastName(json.last_name);
        if (json.first_name && json.last_name) setName(json.first_name + " " + json.last_name);
        if (json.address1) setAddress1(json.address1);
        if (json.city) setCity(json.city);
        if (json.state) setBillingState(json.state);
        if (json.postal_code) setPostalCode(json.postal_code);
        if (json.primary_debtor && json.primary_debtor.email) {
          setEmailField(json.primary_debtor.email);
        }
        if (json.email) {
          setEmailField(json.email);
        }
      } catch (err) {
        // ignore
      }
    };
    fetchClientInfo();
    // eslint-disable-next-line
  }, [caseId]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let payload;
      if (paymentMethod === 'card') {
        payload = {
          reference: `${caseId}`,
          amount,
          email: emailField,
          card_number: cardNumber,
          exp_month: expMonth,
          exp_year: expYear,
          cvv,
          name,
          billing_address1: address1,
          billing_city: city,
          billing_state: billingState,
          billing_postal_code: postalCode
        };
      } else {
        payload = {
          reference: `${caseId}`,
          amount,
          email: emailField,
          method: {
            type: 'bank',
            account_type: accountType,
            holder_type: holderType,
            ...(holderType === 'personal'
              ? { first_name: firstName, last_name: lastName }
              : { business_name: businessName }
            ),
            routing_number: eRouting,
            account_number: eAccount,
            billing_address: {
              address1: address1,
              city: city,
              state: billingState,
              postal_code: postalCode
            }
          }
        };
      }
      const response = await fetch(`${API_URL}/payments/lawpay/charge/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        // Extract a user-friendly message from the gateway response
        let errorMsg = 'Payment failed';
        if (data.detail) {
          try {
            const detailObj = typeof data.detail === 'string'
              ? JSON.parse(data.detail)
              : data.detail;
            if (
              detailObj.messages &&
              Array.isArray(detailObj.messages) &&
              detailObj.messages.length > 0
            ) {
              errorMsg = detailObj.messages[0].message || errorMsg;
            }
          } catch {
            // If parsing fails, fall back to raw detail
            errorMsg = typeof data.detail === 'string'
              ? data.detail
              : errorMsg;
          }
        }
        throw new Error(errorMsg);
      }
      setSuccess(`Payment of $${amount} successful! Transaction ID: ${data.transactionId}`);
      // Notify parent that payment completed
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRequest = async () => {
    const supportedInstruments = [{
      supportedMethods: ['basic-card'],
      data: {
        supportedNetworks: ['visa', 'mastercard', 'amex'],
        supportedTypes: ['debit', 'credit']
      }
    }];
    const details = {
      total: { label: 'Cisowski Law Payment', amount: { currency: 'USD', value: amount.toString() } }
    };
    try {
      const request = new PaymentRequest(supportedInstruments, details);
      const response = await request.show();
      await response.complete('success');
      setSuccess(`Payment of $${amount} via ${response.methodName} successful!`);
      // Notify parent that payment completed
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Digital wallet payment failed.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-0">
      <div className="max-w-md w-full bg-white p-4 sm:p-8 rounded-lg shadow-lg space-y-6 relative">
        {/* Overlays */}
        {loading && (
          <div className="absolute inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-10">
            <span className="text-white text-lg">Processing...</span>
          </div>
        )}
        {success && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 p-6 text-center space-y-4">
            {/* Close button */}
            <button
              onClick={() => setSuccess(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                   viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Success icon */}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-500" fill="none"
                 viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-2xl font-bold text-green-700">Payment Successful!</h3>
            <p className="text-gray-700">
              Thank you for your payment of <strong>${amount}</strong>. Please check your email for your receipt.
            </p>
            <button
              onClick={() => setSuccess(null)}
              className="mt-2 px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 p-4 text-center">
            <h3 className="text-xl font-semibold mb-2">Payment Failed</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-4 px-4 py-2 bg-gray-200 rounded text-gray-700 hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        )}
        <div className="text-center space-y-2">
          <img src="/images/logo.png" alt="Cisowski Law Logo" className="mx-auto mb-4 h-12" />
          <h2 className="text-2xl font-bold mb-4">Cisowski Law Payment</h2>
        </div>
        <p className="mb-4">Case ID: <strong>{caseId}</strong></p>
        <div className="mb-4 flex items-center justify-center">
          <label className="inline-block">
            <span className="text-gray-700 font-medium">Amount (USD)</span>
            <div className="mt-1 relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
              <input
                type="number"
                value={amount}
                onChange={readOnlyAmount ? undefined : (e) => setAmount(e.target.value)}
                readOnly={readOnlyAmount}
                className={`pl-7 w-24 sm:w-32 border-2 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 ${
                  readOnlyAmount
                    ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                    : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>
          </label>
        </div>
        <div className="mb-2">
          <label className="block mb-1">
            <span className="text-gray-700 font-medium">Email</span>
            <input
              type="email"
              value={emailField}
              onChange={(e) => setEmailField(e.target.value)}
              className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              required
              autoComplete="email"
            />
          </label>
        </div>
        <div className="mb-4">
          <span className="font-medium text-gray-700">Payment Method:</span>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                paymentMethod === 'card'
                  ? 'bg-indigo-100 border-indigo-500'
                  : 'bg-white border-gray-300'
              }`}>
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                className="hidden"
                checked={paymentMethod === 'card'}
                onChange={() => setPaymentMethod('card')}
              />
              <span>Card</span>
            </label>
            <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                paymentMethod === 'echeck'
                  ? 'bg-indigo-100 border-indigo-500'
                  : 'bg-white border-gray-300'
              }`}>
              <input
                type="radio"
                name="paymentMethod"
                value="echeck"
                className="hidden"
                checked={paymentMethod === 'echeck'}
                onChange={() => setPaymentMethod('echeck')}
              />
              <span>eCheck</span>
            </label>
          </div>
        </div>
        <form onSubmit={handleSubmit} autoComplete="on" className="space-y-2">
          {paymentMethod === 'card' && (
            <>
              <label className="block mb-1">
                <span className="text-gray-700">Cardholder Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                  autoComplete="cc-name"
                />
              </label>
              <label className="block mb-1">
                <span className="text-gray-700">Card Number</span>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  placeholder="1234 5678 9012 3456"
                  required
                  autoComplete="cc-number"
                />
              </label>
              <div className="flex space-x-2">
                <label className="flex-1">
                  <span className="text-gray-700">Exp Month</span>
                  <input
                    type="text"
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value)}
                    className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    placeholder="MM"
                    required
                    autoComplete="cc-exp-month"
                  />
                </label>
                <label className="flex-1">
                  <span className="text-gray-700">Exp Year</span>
                  <input
                    type="text"
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value)}
                    className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    placeholder="YYYY"
                    required
                    autoComplete="cc-exp-year"
                  />
                </label>
                <label className="flex-1">
                  <span className="text-gray-700">CVV</span>
                  <input
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value)}
                    className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    placeholder="123"
                    required
                    autoComplete="cc-csc"
                  />
                </label>
              </div>
            </>
          )}
          {paymentMethod === 'echeck' && (
            <>
              <div className="mb-4">
                <span className="font-medium text-gray-700">Account Type:</span>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                      accountType === 'CHECKING'
                        ? 'bg-indigo-100 border-indigo-500'
                        : 'bg-white border-gray-300'
                    }`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="CHECKING"
                      className="hidden"
                      checked={accountType === 'CHECKING'}
                      onChange={() => setAccountType('CHECKING')}
                    />
                    <span>Checking</span>
                  </label>
                  <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                      accountType === 'SAVINGS'
                        ? 'bg-indigo-100 border-indigo-500'
                        : 'bg-white border-gray-300'
                    }`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="SAVINGS"
                      className="hidden"
                      checked={accountType === 'SAVINGS'}
                      onChange={() => setAccountType('SAVINGS')}
                    />
                    <span>Savings</span>
                  </label>
                </div>
              </div>
              <div className="mb-4">
                <span className="font-medium text-gray-700">Account Holder:</span>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                      holderType === 'personal'
                        ? 'bg-indigo-100 border-indigo-500'
                        : 'bg-white border-gray-300'
                    }`}>
                    <input
                      type="radio"
                      name="holderType"
                      value="personal"
                      className="hidden"
                      checked={holderType === 'personal'}
                      onChange={() => setHolderType('personal')}
                    />
                    <span>Personal</span>
                  </label>
                  <label className={`flex items-center justify-center p-2 border rounded cursor-pointer text-sm ${
                      holderType === 'business'
                        ? 'bg-indigo-100 border-indigo-500'
                        : 'bg-white border-gray-300'
                    }`}>
                    <input
                      type="radio"
                      name="holderType"
                      value="business"
                      className="hidden"
                      checked={holderType === 'business'}
                      onChange={() => setHolderType('business')}
                    />
                    <span>Business</span>
                  </label>
                </div>
              </div>
              {holderType === 'personal' ? (
                <>
                  <label className="block">
                    <span className="text-gray-700">First Name</span>
                    <input
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="text-gray-700">Last Name</span>
                    <input
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      required
                    />
                  </label>
                </>
              ) : (
                <label className="block">
                  <span className="text-gray-700">Business Name</span>
                  <input
                    type="text"
                    value={businessName}
                    onChange={e => setBusinessName(e.target.value)}
                    className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    required
                  />
                </label>
              )}
              <label className="block">
                <span className="text-gray-700">Routing Number</span>
                <input
                  type="text"
                  value={eRouting}
                  onChange={e => setERouting(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                />
              </label>
              <label className="block">
                <span className="text-gray-700">Account Number</span>
                <input
                  type="text"
                  value={eAccount}
                  onChange={e => setEAccount(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                />
              </label>
            </>
          )}
          <fieldset className="border border-gray-200 p-4 rounded-md bg-gray-50 space-y-4">
            <legend className="text-gray-700 font-semibold">Billing Address</legend>
            <label className="block mb-1">
              <span className="text-gray-700">Street</span>
              <input
                type="text"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                required
                autoComplete="address-line1"
              />
            </label>
            <div className="flex space-x-2">
              <label className="w-1/2">
                <span className="text-gray-700">City</span>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                  autoComplete="address-level2"
                />
              </label>
              <label className="w-1/4">
                <span className="text-gray-700">State</span>
                <input
                  type="text"
                  value={billingState}
                  onChange={(e) => setBillingState(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                  autoComplete="address-level1"
                />
              </label>
              <label className="w-1/4">
                <span className="text-gray-700">ZIP</span>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="mt-1 block w-full bg-white border-2 border-gray-300 rounded-md shadow focus:ring-indigo-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  required
                  autoComplete="postal-code"
                />
              </label>
            </div>
          </fieldset>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </button>
        </form>
<div className="mt-6">
{requiredFees != null && feesPaidPrior != null && (
  <div className="mb-4 p-4 bg-gray-100 rounded-lg">
    <p><strong>Required Total Fees:</strong> ${requiredFees.toFixed(2)}</p>
    <p><strong>Required Retainer:</strong> ${requiredDownPayment.toFixed(2)}</p>
    <p><strong>Total Paid:</strong> ${feesPaidPrior.toFixed(2)}</p>
    <p><strong>Remaining Balance:</strong> ${(requiredFees - feesPaidPrior).toFixed(2)}</p>
  </div>
)}
  <h3 className="text-lg font-semibold mb-2">Recorded Payments</h3>
  {paymentsList.length === 0 ? (
    <p className="text-gray-500">No payments yet.</p>
  ) : (
    <ul className="space-y-2">
      {paymentsList.map(p => (
        <li key={p.id} className="flex justify-between items-center border p-2 rounded">
          <span>
            {p.service_description} — ${(p.payment_amount/100).toFixed(2)} on {new Date(p.payment_date).toLocaleDateString()}
          </span>
        </li>
      ))}
    </ul>
  )}
</div>
      </div>
      
    </div>
    
  );
}
